import { useCallback } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthenticatedFetch from './useAuthenticatedFetch';
import { buildApiUrl } from '../config/apiConfig';

// Default query options (similar to React Query defaults)
const DEFAULT_QUERY_OPTIONS = {
  enabled: false,
  retry: 3,
  staleTime: 5 * 60 * 1000, // 5 minutes
};

// Failed responses carry a JSON body ({ error, message }) that says what went
// wrong in words meant for the person who hit it. Surfacing the raw body — which
// is what a bare `HTTP 400: {"error":...}` message amounts to — puts JSON in
// front of the user, so unpack it here and keep the parts callers need to tell
// one failure from another.
const buildApiError = async (response) => {
  const bodyText = await response.text();
  let payload = null;
  try {
    payload = bodyText ? JSON.parse(bodyText) : null;
  } catch {
    // Not JSON (a proxy error page, say) — fall back to the raw text below.
  }

  const error = new Error(
    payload?.message || bodyText || `Request failed (HTTP ${response.status})`
  );
  error.status = response.status;
  error.code = payload?.error || null;
  error.responseText = bodyText;
  return error;
};

// API request helper. Exported for feature hook files (hooks/useTimesheet.js)
// so they share this one request path rather than growing their own.
export const apiRequest = async (authenticatedFetch, endpoint, options = {}) => {
  const fullUrl = buildApiUrl(endpoint);
  const init = { ...options };
  if (init.body && typeof init.body !== 'string') {
    init.body = JSON.stringify(init.body);
  }

  const response = await authenticatedFetch(fullUrl, init);
  if (!response.ok) {
    throw await buildApiError(response);
  }

  if (response.status === 204) return null;
  return response.json();
};

// API request helper that treats 404 as null
const apiRequestAllow404 = async (authenticatedFetch, endpoint, options = {}) => {
  const fullUrl = buildApiUrl(endpoint);
  const init = { ...options };
  if (init.body && typeof init.body !== 'string') {
    init.body = JSON.stringify(init.body);
  }

  const response = await authenticatedFetch(fullUrl, init);
  if (response.status === 404) return null;

  if (!response.ok) {
    throw await buildApiError(response);
  }

  if (response.status === 204) return null;
  return response.json();
};

export const apiBinaryRequest = async (authenticatedFetch, endpoint, options = {}) => {
  const fullUrl = buildApiUrl(endpoint);
  const init = { ...options };
  if (init.body && typeof init.body !== 'string' && !(init.body instanceof FormData)) {
    init.body = JSON.stringify(init.body);
  }

  const response = await authenticatedFetch(fullUrl, init);
  if (!response.ok) {
    throw await buildApiError(response);
  }

  return {
    blob: await response.blob(),
    contentType: response.headers.get('content-type') || 'application/octet-stream',
    contentDisposition: response.headers.get('content-disposition') || ''
  };
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.slice(result.indexOf(',') + 1) : result;
      resolve(base64);
    };
    reader.readAsDataURL(file);
  });
};

// Test API query hook
export const useTestApi = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['testApi'],
    queryFn: () => apiRequest(authenticatedFetch, '/test'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// Pulls the current employee's TimeCamp hours for a date range. Pass an optional
// `taskId` to restrict the sum to a single task (e.g. Time Off (Paid)).
export const usePullTimeCampHours = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: ({ startDate, endDate, taskId }) => {
      const params = new URLSearchParams();
      params.set('startDate', String(startDate || ''));
      params.set('endDate', String(endDate || ''));
      if (taskId) params.set('taskId', String(taskId));
      return apiRequest(authenticatedFetch, `/timecamp/hours/me?${params.toString()}`);
    },
  });
};

// Admin report: TimeCamp hours per employee for a pay period (reads cache).
export const useTimeCampPayPeriodReport = ({ start, end } = {}, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();

  return useQuery({
    queryKey: ['timecampReport', start || null, end || null],
    queryFn: () =>
      apiRequest(authenticatedFetch, `/timecamp/reports/pay-period${query ? `?${query}` : ''}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: true,
    // TimeCamp hours change frequently, so keep this report fresh: treat cached
    // data as stale after 1 minute (vs the 5-minute default) so a refocus/remount
    // picks up a newer sync quickly instead of showing stale numbers.
    staleTime: 60 * 1000,
    ...options,
  });
};

// Re-poll TimeCamp and refresh the cache for a pay period.
export const useSyncTimeCampPayPeriodReport = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ start, end }) =>
      apiRequest(authenticatedFetch, '/timecamp/reports/pay-period/sync', {
        method: 'POST',
        body: { start, end },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timecampReport'] });
    },
  });
};

// Timesheet validator: live per-employee timesheet summary for a date range.
export const useTimesheetValidator = ({ employeeId, start, end } = {}, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const params = new URLSearchParams();
  if (employeeId) params.set('employeeId', employeeId);
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();

  return useQuery({
    queryKey: ['timesheetValidator', employeeId || null, start || null, end || null],
    queryFn: () =>
      apiRequest(authenticatedFetch, `/timecamp/validator/timesheet${query ? `?${query}` : ''}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(employeeId && start && end) && (options?.enabled ?? true),
    ...options,
  });
};

// Tick or untick one of the reviewer's manual checks for an employee's pay
// period. The response is the check's new state, which is patched straight into
// the cached timesheet rather than refetched: a refetch re-reads TimeCamp, and
// nothing TimeCamp says has changed.
export const useUpdateTimesheetReviewCheck = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, start, end, checkId, checked }) =>
      apiRequest(authenticatedFetch, '/timecamp/validator/manual-checks', {
        method: 'PUT',
        body: { employeeId, start, end, checkId, checked },
      }),
    onSuccess: (check, { employeeId, start, end }) => {
      if (!check?.id) return;
      queryClient.setQueryData(['timesheetValidator', employeeId || null, start || null, end || null], (prev) => {
        if (!prev?.reviewChecks) return prev;
        return {
          ...prev,
          reviewChecks: {
            ...prev.reviewChecks,
            checks: prev.reviewChecks.checks.map((c) => (c.id === check.id ? check : c)),
          },
        };
      });
    },
  });
};

// -----------------------------
// R&D Credits
// -----------------------------

// Persisted R&D credit settings (which buckets count) plus the available bucket
// catalog. Server owns the bucket definitions; we read them at runtime.
export const useRndCreditSettings = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['rdCreditSettings'],
    queryFn: () => apiRequest(authenticatedFetch, '/rd-credits/settings'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

export const useUpdateRndCreditSettings = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config) =>
      apiRequest(authenticatedFetch, '/rd-credits/settings', {
        method: 'PUT',
        body: { config },
      }),
    onSuccess: (data) => {
      // The PUT response is the fresh settings payload; seed the cache with it.
      if (data) queryClient.setQueryData(['rdCreditSettings'], data);
      queryClient.invalidateQueries({ queryKey: ['rdCreditSettings'] });
    },
  });
};

// Pull the tasks present in a range (with entry counts + hours) so the user can
// mark each task's R&D eligibility.
export const useRndCreditTasks = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: ({ start, end, employeeIds }) =>
      apiRequest(authenticatedFetch, '/rd-credits/tasks', {
        method: 'POST',
        body: { start, end, employeeIds: employeeIds || [] },
      }),
  });
};

// -----------------------------
// Saved R&D runs
// -----------------------------
export const useRndCreditRuns = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  return useQuery({
    queryKey: ['rdCreditRuns'],
    queryFn: () => apiRequest(authenticatedFetch, '/rd-credits/runs'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

// Load one saved run (with full result payload) on demand.
export const useGetRndCreditRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  return (id) => apiRequest(authenticatedFetch, `/rd-credits/runs/${encodeURIComponent(id)}`);
};

export const useSaveRndCreditRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiRequest(authenticatedFetch, '/rd-credits/runs', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rdCreditRuns'] }),
  });
};

export const useUpdateRndCreditRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => apiRequest(authenticatedFetch, `/rd-credits/runs/${encodeURIComponent(id)}`, { method: 'PUT', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rdCreditRuns'] }),
  });
};

export const useDeleteRndCreditRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/rd-credits/runs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rdCreditRuns'] }),
  });
};

// Streaming variant of the pull: reads the NDJSON event stream and invokes
// onLog / onProgress as events arrive, resolving with the final result payload.
// For non-streaming responses (validation errors, classifier unavailable) it
// falls back to parsing JSON.
export const useRndPullStream = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return async ({ start, end, employeeIds, caches, phase }, { onLog, onProgress, signal } = {}) => {
    const url = buildApiUrl('/rd-credits/pull');
    const res = await authenticatedFetch(url, {
      method: 'POST',
      body: JSON.stringify({ start, end, employeeIds: employeeIds || [], caches: caches || undefined, phase: phase || undefined }),
      signal,
    });

    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('ndjson') || !res.body) {
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
      return data;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalPayload = null;
    let streamError = null;

    const handleLine = (line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      let evt;
      try { evt = JSON.parse(trimmed); } catch { return; }
      if (evt.type === 'log') onLog?.(evt.message);
      else if (evt.type === 'progress') onProgress?.(evt);
      else if (evt.type === 'result') finalPayload = evt.payload;
      else if (evt.type === 'error') streamError = new Error(evt.message || 'Run failed');
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n')) >= 0) {
        handleLine(buffer.slice(0, idx));
        buffer = buffer.slice(idx + 1);
      }
    }
    if (buffer) handleLine(buffer);

    if (streamError) throw streamError;
    return finalPayload;
  };
};

// Record a manual slush review decision ('include' | 'exclude' | 'reset') for a
// single entry. `entry` carries the snapshot fields the audit table stores.
export const useReviewSlushEntry = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: ({ decision, entry }) =>
      apiRequest(authenticatedFetch, '/rd-credits/slush-review', {
        method: 'POST',
        body: {
          decision,
          reviewKey: entry?.reviewKey,
          bucket: entry?.bucket,
          confidence: entry?.confidence,
          description: entry?.description,
          taskId: entry?.taskId,
          taskName: entry?.taskName,
          user: entry?.user,
          date: entry?.date,
          hours: entry?.hours,
        },
      }),
  });
};

// Record a manual R&D-eligibility decision for a slush DESCRIPTION
// ('include' | 'exclude' | 'reset'). Keyed by description; gates the bucket pass.
export const useReviewRndEligibility = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  return useMutation({
    mutationFn: ({ decision, description }) =>
      apiRequest(authenticatedFetch, '/rd-credits/eligibility-review', {
        method: 'POST',
        body: { decision, description },
      }),
  });
};

// Manually assign a bucket to an Uncategorized entry (writes to the classify cache).
export const useReviewBucketEntry = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  return useMutation({
    mutationFn: ({ cacheKey, description, bucket }) =>
      apiRequest(authenticatedFetch, '/rd-credits/bucket-review', {
        method: 'POST',
        body: { cacheKey, description, bucket },
      }),
  });
};

// Clear one of the R&D caches: 'classification' | 'overrides' | 'slush'.
export const useClearRndCache = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  return useMutation({
    mutationFn: ({ cache }) =>
      apiRequest(authenticatedFetch, '/rd-credits/cache/clear', {
        method: 'POST',
        body: { cache },
      }),
  });
};

// --- BEGIN Hours Breakdown (one-off admin report) ----------------------------
// Self-contained: these two hooks are the only thing in this file that talks to
// /api/hours-breakdown. Deleting this block removes the client's whole data
// layer for the feature. See client/src/pages/HoursBreakdownPage.jsx.

// Who can be picked in the report's employee filter.
export const useHoursBreakdownEmployees = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['hoursBreakdown', 'employees'],
    queryFn: () => apiRequest(authenticatedFetch, '/hours-breakdown/employees'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    // The roster barely moves; the entries below are what needs to be fresh.
    staleTime: 10 * 60 * 1000,
    ...options,
  });
};

// One employee's TimeCamp entries for a date range. Every call is a live pull,
// so this is deliberately not cached for long and never refetches on its own.
export const useHoursBreakdown = ({ employeeIds, start, end } = {}, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  // One report over however many people were picked. Sent as one comma list so
  // the query key is a string and the cache does not miss on array identity.
  const ids = (Array.isArray(employeeIds) ? employeeIds : [employeeIds])
    .filter(Boolean)
    .join(',');

  const params = new URLSearchParams();
  if (ids) params.set('employeeIds', ids);
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();

  return useQuery({
    queryKey: ['hoursBreakdown', 'report', ids || null, start || null, end || null],
    queryFn: () =>
      apiRequest(authenticatedFetch, `/hours-breakdown${query ? `?${query}` : ''}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(ids && start && end) && (options?.enabled ?? true),
    // A TimeCamp round trip is slow enough that re-running it on every remount
    // is worse than showing a minute-old answer next to a Refresh button.
    staleTime: 60 * 1000,
    retry: 1,
    ...options,
  });
};

// The signed-in user's saved report defaults (which tasks they have filtered
// out). Owned by the viewer, not by whoever the report is about.
export const useHoursBreakdownSettings = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['hoursBreakdown', 'settings'],
    queryFn: () => apiRequest(authenticatedFetch, '/hours-breakdown/settings'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

// Save, or (with `clear`) drop back to counting every task. Both write the
// settings cache from the response so the page never re-reads to find out what
// it just stored.
export const useSaveHoursBreakdownSettings = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ excludedTasks = [], clear = false } = {}) =>
      apiRequest(authenticatedFetch, '/hours-breakdown/settings', {
        method: clear ? 'DELETE' : 'PUT',
        ...(clear ? {} : { body: { excludedTasks } }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['hoursBreakdown', 'settings'], data);
    },
  });
};

// Whether the Azure classifier is configured. Asked before the Work Type panel
// offers its button, so the feature explains itself rather than failing on click.
export const useHoursBreakdownClassifyStatus = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['hoursBreakdown', 'classifyStatus'],
    queryFn: () => apiRequest(authenticatedFetch, '/hours-breakdown/classify/status'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
};

// Run the work-type classification. A mutation rather than a query because it
// costs money and must only happen when the user asks for it; the result is held
// in the mutation so leaving the section does not silently re-run it.
export const useClassifyHoursBreakdown = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: ({ employeeIds = [], start, end, excludedTaskIds = [] }) =>
      apiRequest(authenticatedFetch, '/hours-breakdown/classify', {
        method: 'POST',
        body: { employeeIds, start, end, excludedTaskIds },
      }),
  });
};

// Correct one entry's work type by hand. Keyed server-side by task + description,
// so a correction fixes every entry that says the same thing, now and later.
// A null bucket clears the correction and hands the entry back to the AI.
export const useSetHoursBreakdownOverride = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: ({ taskName, parentTaskName, description, bucket }) =>
      apiRequest(authenticatedFetch, '/hours-breakdown/classify/override', {
        method: 'PUT',
        body: { taskName, parentTaskName, description, bucket },
      }),
  });
};
// --- END Hours Breakdown -----------------------------------------------------

// -----------------------------
// Employees
// -----------------------------
export const useEmployees = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['employees'],
    queryFn: () => apiRequest(authenticatedFetch, '/employees'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// Admin: { employees: [{ id, first_name, last_name, job_title, coach_name,
// birthday_month_day ('MM-DD', only when shared), hire_date ('YYYY-MM-DD') }],
// birthdaysWithheld }.
export const useEmployeeCelebrations = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['employees', 'celebrations'],
    queryFn: () => apiRequest(authenticatedFetch, '/employees/celebrations'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useCompanyDirectory = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['employees', 'directory'],
    queryFn: () => apiRequest(authenticatedFetch, '/employees/directory'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useEmployee = (id, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['employees', id],
    queryFn: () => apiRequest(authenticatedFetch, `/employees/${id}`),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useCurrentEmployee = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['employees', 'me'],
    queryFn: () => apiRequest(authenticatedFetch, '/employees/me'),
    ...DEFAULT_QUERY_OPTIONS,
    // An access decision (not invited, deactivated) is a settled answer, not a
    // blip. Retrying it three times only delays the explanation the user needs.
    retry: (failureCount, error) =>
      error?.status >= 400 && error?.status < 500 ? false : failureCount < 3,
    ...options,
  });
};

// Allowed TAWH percentages, owned by the server. Fetched at runtime so
// the client never has to hardcode the list. Enabled by default (config is needed
// on mount) and cached for the session since it changes rarely.
export const useTawhPercents = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['config', 'tawh-percents'],
    queryFn: async () => {
      const data = await apiRequest(authenticatedFetch, '/employees/tawh-percents');
      return Array.isArray(data?.percents) ? data.percents : [];
    },
    ...DEFAULT_QUERY_OPTIONS,
    enabled: true,
    staleTime: Infinity,
    ...options,
  });
};

export const useProfilePhoto = (employeeId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const isMe = !employeeId || employeeId === 'me';
  const endpoint = isMe ? '/employees/me/photo' : `/employees/${employeeId}/photo`;
  const queryKey = isMe ? ['employees', 'me', 'photo'] : ['employees', employeeId, 'photo'];

  return useQuery({
    queryKey,
    queryFn: () => apiRequestAllow404(authenticatedFetch, endpoint),
    enabled: Boolean(options?.enabled ?? true),
    retry: 1,
    staleTime: 60 * 60 * 1000, // 1 hour
    // Kept for the session once fetched, so moving between pages never asks
    // again. Across a reload the browser's own cache answers instead - the
    // server sends photos with a private max-age (see routes/employees.js).
    gcTime: 12 * 60 * 60 * 1000,
    ...options,
  });
};

export const usePayRateHistory = (employeeId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const isMe = !employeeId || employeeId === 'me';
  const endpoint = isMe ? '/employees/me/pay-rates' : `/employees/${employeeId}/pay-rates`;
  const queryKey = isMe ? ['employees', 'me', 'payRates'] : ['employees', employeeId, 'payRates'];

  return useQuery({
    queryKey,
    queryFn: () => apiRequest(authenticatedFetch, endpoint),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// Refreshes the employee-record queries an employee mutation can affect.
//
// Deliberately not awaited, and deliberately `exact`. React Query keeps a
// mutation pending until onSuccess's promise resolves, and invalidateQueries
// doesn't resolve until every matching mounted query has refetched. Since key
// matching is prefix-based, a bare ['employees'] also matches the Graph-backed
// sharepoint/documentLibrary queries — so awaiting it made every save block on
// several SharePoint round trips (times retries). These refetches are cheap DB
// reads and the UI can pick them up as they land.
const invalidateEmployeeRecords = (queryClient, employeeId) => {
  queryClient.invalidateQueries({ queryKey: ['employees'], exact: true });
  queryClient.invalidateQueries({ queryKey: ['employees', 'me'], exact: true });
  queryClient.invalidateQueries({ queryKey: ['employees', 'directory'], exact: true });
  queryClient.invalidateQueries({ queryKey: ['employees', 'coaches'], exact: true });
  if (employeeId) {
    queryClient.invalidateQueries({ queryKey: ['employees', String(employeeId)], exact: true });
  }
  // The payroll roster is its own endpoint under its own key, so it does not
  // come along with the ['employees'] refetches above. Without this a new hire
  // is missing from the payroll picker until the page is reloaded.
  queryClient.invalidateQueries({ queryKey: ['payroll', 'employees'], exact: true });
  // Adding, deactivating and reactivating employees add or withdraw invites.
  queryClient.invalidateQueries({ queryKey: ['admin', 'invites'] });
};

export const useCreatePayRate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, pay_rate, effective_date, notes }) =>
      apiRequest(authenticatedFetch, `/employees/${employeeId}/pay-rates`, {
        method: 'POST',
        body: { pay_rate, effective_date, notes }
      }),
    onSuccess: (_data, variables) => {
      if (variables?.employeeId) {
        queryClient.invalidateQueries({ queryKey: ['employees', variables.employeeId, 'payRates'] });
      }
      invalidateEmployeeRecords(queryClient, variables?.employeeId);
    },
  });
};

export const useUpdatePayRate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, payRateId, pay_rate, effective_date, notes }) =>
      apiRequest(authenticatedFetch, `/employees/${employeeId}/pay-rates/${payRateId}`, {
        method: 'PUT',
        body: { pay_rate, effective_date, notes }
      }),
    onSuccess: (_data, variables) => {
      if (variables?.employeeId) {
        queryClient.invalidateQueries({ queryKey: ['employees', variables.employeeId, 'payRates'] });
      }
      invalidateEmployeeRecords(queryClient, variables?.employeeId);
    },
  });
};

export const useDeletePayRate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, payRateId }) =>
      apiRequest(authenticatedFetch, `/employees/${employeeId}/pay-rates/${payRateId}`, {
        method: 'DELETE'
      }),
    onSuccess: (_data, variables) => {
      if (variables?.employeeId) {
        queryClient.invalidateQueries({ queryKey: ['employees', variables.employeeId, 'payRates'] });
      }
      invalidateEmployeeRecords(queryClient, variables?.employeeId);
    },
  });
};

// -----------------------------
// Commitments (dated TAWH % / target hours / classification)
// -----------------------------
// Newest first: [{ id, effective_date 'YYYY-MM-DD', is_part_time,
// tawh_percent, target_hours, notes, created_by_name, created_at }].
export const useCommitmentHistory = (employeeId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const isMe = !employeeId || employeeId === 'me';
  const endpoint = isMe ? '/employees/me/commitments' : `/employees/${employeeId}/commitments`;
  const queryKey = isMe ? ['employees', 'me', 'commitments'] : ['employees', employeeId, 'commitments'];

  return useQuery({
    queryKey,
    queryFn: () => apiRequest(authenticatedFetch, endpoint),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// A commitment change re-measures hours, so besides the history and the
// employee record (whose current figures mirror it) the timesheet validator's
// cached answers are stale too.
const invalidateCommitments = (queryClient, employeeId) => {
  if (employeeId) {
    queryClient.invalidateQueries({ queryKey: ['employees', employeeId, 'commitments'] });
  }
  queryClient.invalidateQueries({ queryKey: ['employees', 'me', 'commitments'] });
  queryClient.invalidateQueries({ queryKey: ['timesheetValidator'] });
  invalidateEmployeeRecords(queryClient, employeeId);
};

export const useCreateCommitment = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, ...body }) =>
      apiRequest(authenticatedFetch, `/employees/${employeeId}/commitments`, { method: 'POST', body }),
    onSuccess: (_data, variables) => invalidateCommitments(queryClient, variables?.employeeId),
  });
};

export const useUpdateCommitment = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, commitmentId, ...body }) =>
      apiRequest(authenticatedFetch, `/employees/${employeeId}/commitments/${commitmentId}`, { method: 'PUT', body }),
    onSuccess: (_data, variables) => invalidateCommitments(queryClient, variables?.employeeId),
  });
};

export const useDeleteCommitment = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, commitmentId }) =>
      apiRequest(authenticatedFetch, `/employees/${employeeId}/commitments/${commitmentId}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => invalidateCommitments(queryClient, variables?.employeeId),
  });
};

// -----------------------------
// Resume
// -----------------------------
export const useResume = (employeeId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const isMe = !employeeId || employeeId === 'me';
  const endpoint = isMe ? '/employees/me/resume' : `/employees/${employeeId}/resume`;
  const queryKey = isMe ? ['employees', 'me', 'resume'] : ['employees', employeeId, 'resume'];

  return useQuery({
    queryKey,
    queryFn: () => apiRequest(authenticatedFetch, endpoint),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

export const useSaveResume = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, ...resume }) => {
      const isMe = !employeeId || employeeId === 'me';
      const endpoint = isMe ? '/employees/me/resume' : `/employees/${employeeId}/resume`;
      return apiRequest(authenticatedFetch, endpoint, { method: 'PUT', body: resume });
    },
    onSuccess: async (_data, variables) => {
      const employeeId = variables?.employeeId;
      const isMe = !employeeId || employeeId === 'me';
      if (isMe) {
        await queryClient.invalidateQueries({ queryKey: ['employees', 'me', 'resume'] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['employees', employeeId, 'resume'] });
      }
      // Also refresh the admin aggregate (Manage Resumes) so it doesn't go stale.
      await queryClient.invalidateQueries({ queryKey: ['resumes', 'all'] });
    },
  });
};

// Admin: all resume data across employees (flattened per category).
export const useAllResumes = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['resumes', 'all'],
    queryFn: () => apiRequest(authenticatedFetch, '/resumes'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

export const useCoachOptions = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['employees', 'coaches'],
    queryFn: async () => {
      const employees = await apiRequest(authenticatedFetch, '/employees');
      return (employees || [])
        .filter((e) => e?.is_active !== false)
        .map((e) => ({
          label: `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email,
          value: e.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    ...DEFAULT_QUERY_OPTIONS,
    enabled: true,
    ...options,
  });
};

export const useCreateEmployee = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => apiRequest(authenticatedFetch, '/employees', { method: 'POST', body: payload }),
    onSuccess: () => {
      invalidateEmployeeRecords(queryClient);
    },
  });
};

// Find the Entra account behind an email address. The OID an employee record
// needs is on the answer, so nobody has to go and find one to type in.
export const useEntraUserByEmail = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: (email) =>
      apiRequest(authenticatedFetch, `/employees/entra-user?email=${encodeURIComponent(String(email || '').trim())}`),
  });
};

export const useEntraUserLookup = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: (oid) => apiRequest(authenticatedFetch, `/employees/entra-user/${encodeURIComponent(oid)}`),
  });
};

export const useSharePointSiteContext = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['employees', 'sharepoint', 'context'],
    queryFn: () => apiRequest(authenticatedFetch, '/employees/sharepoint/context'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useSharePointDrives = (siteId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['employees', 'sharepoint', 'drives', siteId || ''],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/employees/sharepoint/drives?siteId=${encodeURIComponent(String(siteId || ''))}`
      ),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(siteId) && (options?.enabled ?? true),
    ...options,
  });
};

export const useSharePointFolders = (siteId, driveId, path = '', options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const normalizedPath = String(path || '');

  return useQuery({
    queryKey: ['employees', 'sharepoint', 'folders', siteId || '', driveId || '', normalizedPath],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/employees/sharepoint/folders?siteId=${encodeURIComponent(String(siteId || ''))}&driveId=${encodeURIComponent(String(driveId || ''))}&path=${encodeURIComponent(normalizedPath)}`
      ),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(siteId && driveId) && (options?.enabled ?? true),
    ...options,
  });
};

export const useCreateSharePointFolder = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteId, driveId, path, folderName }) =>
      apiRequest(authenticatedFetch, '/employees/sharepoint/folders', {
        method: 'POST',
        body: { siteId, driveId, path, folderName }
      }),
    onSuccess: async (_data, variables) => {
      const siteId = String(variables?.siteId || '');
      const driveId = String(variables?.driveId || '');
      const path = String(variables?.path || '');
      await queryClient.invalidateQueries({
        queryKey: ['employees', 'sharepoint', 'folders', siteId, driveId, path]
      });
    },
  });
};

export const useDocumentLibraryContext = (employeeId = 'me', options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const normalizedEmployeeId = String(employeeId || 'me');

  return useQuery({
    queryKey: ['employees', 'documentLibrary', 'context', normalizedEmployeeId],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/employees/document-library/context?employeeId=${encodeURIComponent(normalizedEmployeeId)}`
      ),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(normalizedEmployeeId) && (options?.enabled ?? true),
    ...options,
  });
};

export const useDocumentLibraryItems = (employeeId = 'me', path = '', options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const normalizedEmployeeId = String(employeeId || 'me');
  const normalizedPath = String(path || '');

  return useQuery({
    queryKey: ['employees', 'documentLibrary', 'items', normalizedEmployeeId, normalizedPath],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/employees/document-library/items?employeeId=${encodeURIComponent(normalizedEmployeeId)}&path=${encodeURIComponent(normalizedPath)}`
      ),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(normalizedEmployeeId) && (options?.enabled ?? true),
    ...options,
  });
};

export const useCreateDocumentLibraryFolder = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId = 'me', path = '', folderName }) =>
      apiRequest(authenticatedFetch, '/employees/document-library/folders', {
        method: 'POST',
        body: { employeeId, path, folderName },
      }),
    onSuccess: async (_data, variables) => {
      const normalizedEmployeeId = String(variables?.employeeId || 'me');
      const normalizedPath = String(variables?.path || '');
      await queryClient.invalidateQueries({
        queryKey: ['employees', 'documentLibrary', 'items', normalizedEmployeeId, normalizedPath],
      });
    },
  });
};

export const useUploadDocumentLibraryItem = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ employeeId = 'me', path = '', file }) => {
      if (!file) throw new Error('No file selected');

      const content_base64 = await fileToBase64(file);
      return apiRequest(authenticatedFetch, '/employees/document-library/upload', {
        method: 'POST',
        body: {
          employeeId,
          path,
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          content_base64,
        },
      });
    },
    onSuccess: async (_data, variables) => {
      const normalizedEmployeeId = String(variables?.employeeId || 'me');
      const normalizedPath = String(variables?.path || '');
      await queryClient.invalidateQueries({
        queryKey: ['employees', 'documentLibrary', 'items', normalizedEmployeeId, normalizedPath],
      });
    },
  });
};

export const useDocumentLibraryItemContent = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: ({ employeeId = 'me', itemId, fileName, download = false }) => {
      if (!itemId) throw new Error('Missing document item id');
      const suffix = download ? 'true' : 'false';
      return apiBinaryRequest(
        authenticatedFetch,
        `/employees/document-library/items/${encodeURIComponent(itemId)}/content?employeeId=${encodeURIComponent(String(employeeId || 'me'))}&download=${suffix}&fileName=${encodeURIComponent(String(fileName || 'document'))}`
      );
    },
  });
};

export const useUpdateEmployee = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => apiRequest(authenticatedFetch, `/employees/${id}`, { method: 'PUT', body: payload }),
    onSuccess: (_data, variables) => {
      invalidateEmployeeRecords(queryClient, variables?.id);

      // The document library only needs a (slow, Graph-backed) refetch when the
      // drive assignment itself changed — not when any other field was edited.
      const changedDocumentLibrary =
        Object.prototype.hasOwnProperty.call(variables || {}, 'sharepoint_drive_id') ||
        Object.prototype.hasOwnProperty.call(variables || {}, 'document_library_id');
      if (!changedDocumentLibrary) return;

      for (const target of [String(variables?.id || ''), 'me']) {
        if (!target) continue;
        queryClient.invalidateQueries({ queryKey: ['employees', 'documentLibrary', 'context', target] });
        queryClient.invalidateQueries({ queryKey: ['employees', 'documentLibrary', 'items', target] });
      }
    },
  });
};

// Despite the name this deactivates rather than deletes — DELETE /employees/:id
// sets is_active = false. Permanent removal is usePermanentlyDeleteEmployee.
export const useDeleteEmployee = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/employees/${id}`, { method: 'DELETE' }),
    onSuccess: (_data, id) => {
      invalidateEmployeeRecords(queryClient, id);
    },
  });
};

// What permanent deletion would remove. Fetched when the confirmation dialog
// opens and never cached, so the counts shown are the ones being agreed to.
export const useEmployeeDeleteImpact = (employeeId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['employees', employeeId, 'delete-impact'],
    queryFn: () => apiRequest(authenticatedFetch, `/employees/${employeeId}/delete-impact`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: !!employeeId,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    ...options,
  });
};

export const usePermanentlyDeleteEmployee = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/employees/${id}/permanent`, { method: 'DELETE' }),
    onSuccess: (_data, id) => {
      invalidateEmployeeRecords(queryClient, id);
      // The row is gone rather than changed, and its records were spread across
      // time off, expenses and approvals, so refetch broadly instead of guessing.
      queryClient.invalidateQueries();
    },
  });
};

// -----------------------------
// Time Off
// -----------------------------
export const useTimeOffRequests = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['timeOff'],
    queryFn: () => apiRequest(authenticatedFetch, '/time-off'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// Saved Vacation Planner plans - the signed-in employee's own, newest first:
// [{ id, name, start_date, end_date, inputs, summary, created_at, updated_at }].
export const useVacationPlans = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['vacationPlans'],
    queryFn: () => apiRequest(authenticatedFetch, '/vacation-plans'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// Creates a plan, or updates one when the payload carries an id.
export const useSaveVacationPlan = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }) => (id
      ? apiRequest(authenticatedFetch, `/vacation-plans/${encodeURIComponent(id)}`, { method: 'PUT', body })
      : apiRequest(authenticatedFetch, '/vacation-plans', { method: 'POST', body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vacationPlans'] }),
  });
};

// Records requests submitted from a plan: { id, request_ids, total }.
export const useRecordPlanSubmission = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }) => apiRequest(
      authenticatedFetch,
      `/vacation-plans/${encodeURIComponent(id)}/submission`,
      { method: 'PUT', body }
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vacationPlans'] }),
  });
};

export const useDeleteVacationPlan = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/vacation-plans/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vacationPlans'] }),
  });
};

export const useTimeOffRequest = (id, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['timeOff', id],
    queryFn: () => apiRequest(authenticatedFetch, `/time-off/${id}`),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useCreateTimeOffRequest = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => apiRequest(authenticatedFetch, '/time-off', { method: 'POST', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['timeOff'] });
    },
  });
};

export const useResubmitTimeOffRequest = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => apiRequest(authenticatedFetch, `/time-off/${id}/resubmit`, { method: 'POST', body: payload }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['timeOff'] });
      if (variables?.id) {
        await queryClient.invalidateQueries({ queryKey: ['timeOff', variables.id] });
      }
    },
  });
};

export const useUpdateTimeOffRequest = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => apiRequest(authenticatedFetch, `/time-off/${id}`, { method: 'PUT', body: payload }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['timeOff'] });
      if (variables?.id) {
        await queryClient.invalidateQueries({ queryKey: ['timeOff', variables.id] });
      }
    },
  });
};

export const useApproveTimeOffRequest = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approval_reason }) =>
      apiRequest(authenticatedFetch, `/time-off/${id}/approve`, { method: 'PUT', body: { approval_reason } }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['timeOff'] });
      if (variables?.id) await queryClient.invalidateQueries({ queryKey: ['timeOff', variables.id] });
    },
  });
};

export const useRejectTimeOffRequest = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes, approval_reason }) =>
      apiRequest(authenticatedFetch, `/time-off/${id}/reject`, { method: 'PUT', body: { notes, approval_reason } }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['timeOff'] });
      if (variables?.id) await queryClient.invalidateQueries({ queryKey: ['timeOff', variables.id] });
    },
  });
};

// Saves an approver comment without approving or denying the request.
export const useCommentTimeOffRequest = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approval_reason }) =>
      apiRequest(authenticatedFetch, `/time-off/${id}/comment`, { method: 'PUT', body: { approval_reason } }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['timeOff'] });
      if (variables?.id) queryClient.invalidateQueries({ queryKey: ['timeOff', variables.id] });
    },
  });
};

export const usePendingTimeOffRequests = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['timeOff', 'pending'],
    queryFn: () => apiRequest(authenticatedFetch, '/time-off'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useCancelTimeOffRequest = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/time-off/${id}`, { method: 'DELETE' }),
    onSuccess: async (_data, id) => {
      await queryClient.invalidateQueries({ queryKey: ['timeOff'] });
      if (id) await queryClient.invalidateQueries({ queryKey: ['timeOff', id] });
    },
  });
};

// -----------------------------
// Close Dates
// -----------------------------
export const useCloseDates = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['closeDates'],
    queryFn: () => apiRequest(authenticatedFetch, '/close-dates'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useCreateCloseDate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => apiRequest(authenticatedFetch, '/close-dates', { method: 'POST', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['closeDates'] });
    },
  });
};

export const useUpdateCloseDate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => apiRequest(authenticatedFetch, `/close-dates/${id}`, { method: 'PUT', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['closeDates'] });
    },
  });
};

export const useDeleteCloseDate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/close-dates/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['closeDates'] });
    },
  });
};

// -----------------------------
// Hiring Candidates
// -----------------------------
export const useHiringCandidates = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['hiringCandidates'],
    queryFn: () => apiRequest(authenticatedFetch, '/hiring-candidates'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useHiringStaffOptions = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['hiringCandidates', 'staffOptions'],
    queryFn: () => apiRequest(authenticatedFetch, '/hiring-candidates/staff-options'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useCreateHiringCandidate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => apiRequest(authenticatedFetch, '/hiring-candidates', { method: 'POST', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hiringCandidates'] });
    },
  });
};

export const useUpdateHiringCandidate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => apiRequest(authenticatedFetch, `/hiring-candidates/${id}`, { method: 'PUT', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hiringCandidates'] });
    },
  });
};

export const useDeleteHiringCandidate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/hiring-candidates/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hiringCandidates'] });
    },
  });
};

// -----------------------------
// Onboarding
// -----------------------------

// The signed-in employee's own journey. Returns `assigned: false` for everybody
// who is not onboarding, which is what hides the page and the nav item.
export const useMyOnboarding = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['onboarding', 'me'],
    queryFn: () => apiRequest(authenticatedFetch, '/onboarding/me'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// The response is the whole updated journey, so it is written straight into the
// cache: the page redraws on the answer rather than on a refetch of it.
export const useCompleteOnboardingStep = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    // An input step sends its answer with the confirmation, and a signature
    // step the name typed: one button on the page, one write on the server.
    // Every other kind sends nothing.
    mutationFn: ({ stepId, responseText }) =>
      apiRequest(authenticatedFetch, `/onboarding/me/steps/${stepId}/complete`, {
        method: 'POST',
        body: responseText == null ? undefined : { response_text: responseText },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['onboarding', 'me'], data);
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
};

// What a profile step can ask for. Server-owned: the same catalogue decides
// which columns get checked, so the editor offers exactly what is measured.
// One box ticked, applied to a cached journey. Used to answer the click before
// the server does — waiting on a round trip to move a checkbox makes ticking a
// list feel broken.
const applyChecklistTick = (state, stepId, itemId, checked) => {
  if (!state?.steps) return state;

  return {
    ...state,
    steps: state.steps.map((step) => {
      if (step.id !== stepId || !step.checklist_status) return step;

      const items = step.checklist_status.items.map((item) => (
        item.id === itemId ? { ...item, checked } : item
      ));

      return {
        ...step,
        checklist_status: {
          ...step.checklist_status,
          items,
          checked_count: items.filter((item) => item.checked).length,
          complete: items.length > 0 && items.every((item) => item.checked),
        },
      };
    }),
  };
};

// Tick or untick one box on a checklist step. Applied to the cache immediately
// and rolled back if the save fails; the response is the whole updated journey,
// which then replaces the guess.
export const useSetOnboardingChecklistItem = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ stepId, itemId, checked }) =>
      apiRequest(authenticatedFetch, `/onboarding/me/steps/${stepId}/items/${itemId}`, {
        method: 'PUT',
        body: { checked },
      }),
    onMutate: async ({ stepId, itemId, checked }) => {
      // An in-flight refetch would land after this and undo it.
      await queryClient.cancelQueries({ queryKey: ['onboarding', 'me'] });
      const previous = queryClient.getQueryData(['onboarding', 'me']);
      queryClient.setQueryData(['onboarding', 'me'], (old) => applyChecklistTick(old, stepId, itemId, checked));
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(['onboarding', 'me'], context.previous);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['onboarding', 'me'], data);
    },
  });
};

// The date on an event step. Like a tick, the response is the whole journey.
export const useSetOnboardingStepSchedule = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ stepId, scheduledOn }) =>
      apiRequest(authenticatedFetch, `/onboarding/me/steps/${stepId}/schedule`, {
        method: 'PUT',
        body: { scheduled_on: scheduledOn },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['onboarding', 'me'], data);
    },
  });
};

// What one employee wrote on their input steps, for the admin table. Fetched
// only when a row is opened - the answers are paragraphs, and the list itself
// only needs the count it already has.
export const useOnboardingResponses = (employeeId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['onboarding', 'responses', employeeId || null],
    queryFn: () =>
      apiRequest(authenticatedFetch, `/onboarding/assignments/${encodeURIComponent(employeeId)}/responses`),
    // Spread first, then enabled: DEFAULT_QUERY_OPTIONS carries enabled:false,
    // so spreading it last would switch this query off and leave the dialog
    // reading an empty result as "there are no questions".
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(employeeId) && (options.enabled ?? true),
  });
};

// Emails the employee's coach that they are through onboarding. Answers
// { notified, reason } rather than failing when there is no coach to tell.
export const useNotifyOnboardingCoach = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: () => apiRequest(authenticatedFetch, '/onboarding/me/notify-coach', { method: 'POST' }),
  });
};

export const useOnboardingProfileGroups = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['onboarding', 'profileGroups'],
    queryFn: () => apiRequest(authenticatedFetch, '/onboarding/profile-groups'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useOnboardingSteps = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['onboarding', 'steps'],
    queryFn: () => apiRequest(authenticatedFetch, '/onboarding/steps'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useCreateOnboardingStep = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => apiRequest(authenticatedFetch, '/onboarding/steps', { method: 'POST', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
};

export const useUpdateOnboardingStep = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      apiRequest(authenticatedFetch, `/onboarding/steps/${id}`, { method: 'PUT', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
};

export const useDeleteOnboardingStep = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/onboarding/steps/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
};

// Takes the full ordered list of step ids: the server rewrites every position
// in one go rather than swapping a pair, so a half list is rejected.
export const useReorderOnboardingSteps = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids) => apiRequest(authenticatedFetch, '/onboarding/steps/reorder', { method: 'PUT', body: { ids } }),
    onSuccess: (data) => {
      queryClient.setQueryData(['onboarding', 'steps'], data);
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
};

// Every active employee with their onboarding row when they have one — the
// people in the journey and the people who could be put into it, in one list.
export const useOnboardingAssignments = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['onboarding', 'assignments'],
    queryFn: () => apiRequest(authenticatedFetch, '/onboarding/assignments'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useOnboardingCandidates = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['onboarding', 'candidates'],
    queryFn: () => apiRequest(authenticatedFetch, '/onboarding/candidates'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useAssignOnboarding = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, hiringCandidateId = null }) =>
      apiRequest(authenticatedFetch, '/onboarding/assignments', {
        method: 'POST',
        body: { employee_id: employeeId, hiring_candidate_id: hiringCandidateId },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      // Assigning from the hiring board moves the candidate to Onboarding Started.
      await queryClient.invalidateQueries({ queryKey: ['hiringCandidates'] });
    },
  });
};

export const useResetOnboarding = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeId) =>
      apiRequest(authenticatedFetch, `/onboarding/assignments/${employeeId}/reset`, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
};

export const useUnassignOnboarding = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeId) =>
      apiRequest(authenticatedFetch, `/onboarding/assignments/${employeeId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      await queryClient.invalidateQueries({ queryKey: ['hiringCandidates'] });
    },
  });
};

// Upload a file on the employee's current upload step. It lands in their
// document library's Onboarding folder; the response is the whole journey.
export const useUploadOnboardingFile = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ stepId, file }) => {
      if (!file) throw new Error('No file selected');
      const content_base64 = await fileToBase64(file);
      return apiRequest(authenticatedFetch, `/onboarding/me/steps/${stepId}/uploads`, {
        method: 'POST',
        body: { file_name: file.name, content_base64 },
      });
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(['onboarding', 'me'], data);
      // The file is in their library now too.
      await queryClient.invalidateQueries({ queryKey: ['employees', 'documentLibrary'] });
    },
  });
};

export const useRemoveOnboardingUpload = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ stepId, uploadId }) =>
      apiRequest(authenticatedFetch, `/onboarding/me/steps/${stepId}/uploads/${uploadId}`, { method: 'DELETE' }),
    onSuccess: async (data) => {
      queryClient.setQueryData(['onboarding', 'me'], data);
      await queryClient.invalidateQueries({ queryKey: ['employees', 'documentLibrary'] });
    },
  });
};

// One file an employee uploaded, for the admin's responses dialog.
export const useOnboardingUploadContent = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: ({ employeeId, uploadId }) =>
      apiBinaryRequest(
        authenticatedFetch,
        `/onboarding/assignments/${encodeURIComponent(employeeId)}/uploads/${encodeURIComponent(uploadId)}/content`
      ),
  });
};

// -----------------------------
// Expenses
// -----------------------------
export const useExpenses = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  // `mine: true` forces the current user's own expenses only, even for
  // finance/admin (used by the personal "My Expenses" page). Without it the
  // endpoint returns every expense for finance/admin (used by the approval and
  // payroll views). The query key differs so the two responses don't collide in
  // the cache; mutations invalidate the ['expenses'] prefix, covering both.
  const { mine = false, ...queryOptions } = options;

  return useQuery({
    queryKey: mine ? ['expenses', 'mine'] : ['expenses'],
    queryFn: () => apiRequest(authenticatedFetch, mine ? '/expenses?mine=true' : '/expenses'),
    ...DEFAULT_QUERY_OPTIONS,
    ...queryOptions,
  });
};

export const useExpense = (id, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['expenses', id],
    queryFn: () => apiRequest(authenticatedFetch, `/expenses/${id}`),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useExpenseVendors = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['expenses', 'meta', 'vendors'],
    queryFn: () => apiRequest(authenticatedFetch, '/expenses/meta/vendors'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useExpenseCustomers = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['expenses', 'meta', 'customers'],
    queryFn: () => apiRequest(authenticatedFetch, '/expenses/meta/customers'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// The pre-approved All Hands meal: { category, amount }. The amount is
// server-owned (server/config/expenseConfig.js); the form only displays it.
export const useExpenseAllHandsMeal = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['expenses', 'meta', 'all-hands-meal'],
    queryFn: () => apiRequest(authenticatedFetch, '/expenses/meta/all-hands-meal'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// Every mileage rate, oldest first: [{ effective_date, rate_per_mile }]. The
// form prices miles with them as they're typed; the server reprices on save.
export const useExpenseMileageRates = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['expenses', 'meta', 'mileage-rates'],
    queryFn: () => apiRequest(authenticatedFetch, '/expenses/meta/mileage-rates'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// Admin mileage rate history, newest first, with who entered or changed each.
export const useMileageRatesAdmin = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['expenses', 'admin', 'mileage-rates'],
    queryFn: () => apiRequest(authenticatedFetch, '/expenses/admin/mileage-rates'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

// Saves a rate: updates when `id` is given, otherwise adds one.
export const useSaveMileageRate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }) => apiRequest(
      authenticatedFetch,
      id ? `/expenses/admin/mileage-rates/${id}` : '/expenses/admin/mileage-rates',
      { method: id ? 'PUT' : 'POST', body }
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses', 'admin', 'mileage-rates'] });
      await queryClient.invalidateQueries({ queryKey: ['expenses', 'meta', 'mileage-rates'] });
    },
  });
};

export const useDeleteMileageRate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/expenses/admin/mileage-rates/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses', 'admin', 'mileage-rates'] });
      await queryClient.invalidateQueries({ queryKey: ['expenses', 'meta', 'mileage-rates'] });
    },
  });
};

// Who may sign in: pending invites and the invites already used.
export const useInvites = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => apiRequest(authenticatedFetch, '/admin/invites'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useAddInvite = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email) => apiRequest(authenticatedFetch, '/admin/invites', { method: 'POST', body: { email } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'invites'] }),
  });
};

export const useRemoveInvite = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/admin/invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'invites'] }),
  });
};

export const useCreateExpense = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => apiRequest(authenticatedFetch, '/expenses', { method: 'POST', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

// Admin vendor/customer management. `kind` is 'vendors' or 'customers'. Rows
// come back as { id, name, expense_count }.
export const useExpenseLookupAdmin = (kind, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['expenses', 'admin', kind],
    queryFn: () => apiRequest(authenticatedFetch, `/expenses/admin/${kind}`),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useRenameExpenseLookup = (kind) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }) =>
      apiRequest(authenticatedFetch, `/expenses/admin/${kind}/${id}`, { method: 'PUT', body: { name } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

export const useConsolidateExpenseLookup = (kind) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, targetId }) =>
      apiRequest(authenticatedFetch, `/expenses/admin/${kind}/consolidate`, {
        method: 'POST',
        body: { ids, targetId },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

export const useUpdateExpense = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => apiRequest(authenticatedFetch, `/expenses/${id}`, { method: 'PUT', body: payload }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (variables?.id) {
        await queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
      }
    },
  });
};

export const useCancelExpense = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/expenses/${id}`, { method: 'DELETE' }),
    onSuccess: async (_data, id) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (id) {
        await queryClient.invalidateQueries({ queryKey: ['expenses', id] });
      }
    },
  });
};

export const useApproveExpense = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approval_reason }) =>
      apiRequest(authenticatedFetch, `/expenses/${id}/approve`, { method: 'PUT', body: { approval_reason } }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (variables?.id) await queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

export const useRejectExpense = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approval_reason }) =>
      apiRequest(authenticatedFetch, `/expenses/${id}/reject`, { method: 'PUT', body: { approval_reason } }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (variables?.id) await queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

// Starts a batch. Expenses join it as they are saved; nothing reaches the
// approval queue until the group itself is submitted.
export const useCreateExpenseGroup = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    // `employee_id` starts the batch for someone else; the server checks the
    // permission and owns the batch to them, with the caller kept as created_by.
    mutationFn: ({ name, employee_id = null }) =>
      apiRequest(authenticatedFetch, '/expenses/groups', {
        method: 'POST',
        body: { name, employee_id },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

// Sends a saved (draft) expense for approval.
export const useSubmitExpense = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId }) => {
      if (!expenseId) throw new Error('Missing expense id');
      return apiRequest(authenticatedFetch, `/expenses/${expenseId}/submit`, { method: 'PUT' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

// Moves an expense into a batch, or out of one when groupId is null.
export const useMoveExpenseToGroup = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, groupId = null }) => {
      if (!expenseId) throw new Error('Missing expense id');
      return apiRequest(authenticatedFetch, `/expenses/${expenseId}/group`, {
        method: 'PATCH',
        body: { group_id: groupId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

// Renames a batch. Expenses carry their group's name, so the whole list is
// refetched rather than the group alone.
export const useRenameExpenseGroup = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }) => {
      if (!id) throw new Error('Missing group id');
      return apiRequest(authenticatedFetch, `/expenses/groups/${id}`, {
        method: 'PATCH',
        body: { name },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

export const useSubmitExpenseGroup = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) =>
      apiRequest(authenticatedFetch, `/expenses/groups/${id}/submit`, { method: 'PUT' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

// Saves a reviewer comment without approving or rejecting the expense.
export const useCommentExpense = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approval_reason }) =>
      apiRequest(authenticatedFetch, `/expenses/${id}/comment`, { method: 'PUT', body: { approval_reason } }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (variables?.id) queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

export const useMarkExpensePaid = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, paid_on }) =>
      apiRequest(authenticatedFetch, `/expenses/${id}/paid-on`, { method: 'PUT', body: { paid_on } }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (variables?.id) await queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

// Bills a settled customer reimbursable expense to its customer. The server
// refuses anything that isn't reimbursable, isn't settled, or was invoiced
// already, so there is nothing to check here.
export const useMarkExpenseInvoiced = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, invoiced_on, invoice_cap_amount = null }) =>
      apiRequest(authenticatedFetch, `/expenses/${id}/invoiced-on`, {
        method: 'PUT',
        body: { invoiced_on, invoice_cap_amount },
      }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (variables?.id) await queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

// Moves an expense back down the payroll board, clearing whatever the buckets
// it is leaving had recorded. One call however many buckets it crosses, so a
// two-step revert cannot come apart halfway.
export const useRevertExpenseStage = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, stage }) =>
      apiRequest(authenticatedFetch, `/expenses/${id}/revert-stage`, { method: 'PUT', body: { stage } }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (variables?.id) await queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

// Corrects the cap on an expense that is already invoiced. Separate from
// useMarkExpenseInvoiced because that one moves the expense between buckets and
// this one only edits a figure on it.
export const useSetExpenseInvoiceCap = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, invoice_cap_amount = null }) =>
      apiRequest(authenticatedFetch, `/expenses/${id}/invoice-cap`, {
        method: 'PUT',
        body: { invoice_cap_amount },
      }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (variables?.id) await queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

export const useSetExpenseQboReceipt = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, receipt_uploaded_to_qbo }) =>
      apiRequest(authenticatedFetch, `/expenses/${id}/qbo-receipt`, {
        method: 'PUT',
        body: { receipt_uploaded_to_qbo },
      }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      if (variables?.id) await queryClient.invalidateQueries({ queryKey: ['expenses', variables.id] });
    },
  });
};

export const useExpenseAttachments = (expenseId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['expenses', expenseId, 'attachments'],
    queryFn: () => apiRequest(authenticatedFetch, `/expenses/${expenseId}/attachments`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(expenseId) && (options?.enabled ?? true),
    ...options,
  });
};

export const useUploadExpenseAttachment = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    // `isReceipt`/`receiptAmount` carry the tagging the new expense form
    // collected while the file was still local, so it lands with the upload
    // rather than needing a second call once the row exists.
    mutationFn: async ({ expenseId, file, isReceipt = false, receiptAmount = null }) => {
      if (!expenseId) throw new Error('Missing expense id');
      if (!file) throw new Error('No file selected');

      const content_base64 = await fileToBase64(file);
      return apiRequest(authenticatedFetch, `/expenses/${expenseId}/attachments`, {
        method: 'POST',
        body: {
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          content_base64,
          is_receipt: isReceipt === true,
          receipt_amount: receiptAmount
        }
      });
    },
    // Deliberately not awaited: awaiting an invalidation also waits for every
    // active query to refetch, which put a full expenses-list round trip
    // between each file of a multi-file upload. The refetch still happens, it
    // just isn't on the critical path.
    onSuccess: (_data, variables) => {
      if (variables?.expenseId) {
        queryClient.invalidateQueries({ queryKey: ['expenses', variables.expenseId, 'attachments'] });
      }
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

export const useExpenseAttachmentContent = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: ({ expenseId, attachmentId, download = false }) => {
      if (!expenseId || !attachmentId) throw new Error('Missing attachment target');
      const suffix = download ? '?download=true' : '';
      return apiBinaryRequest(authenticatedFetch, `/expenses/${expenseId}/attachments/${attachmentId}/content${suffix}`);
    },
  });
};

export const useDeleteExpenseAttachment = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, attachmentId }) => {
      if (!expenseId || !attachmentId) throw new Error('Missing attachment target');
      return apiRequest(authenticatedFetch, `/expenses/${expenseId}/attachments/${attachmentId}`, { method: 'DELETE' });
    },
    onSuccess: async (_data, variables) => {
      if (variables?.expenseId) {
        await queryClient.invalidateQueries({ queryKey: ['expenses', variables.expenseId, 'attachments'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
};

// Changes an existing attachment's receipt tagging (is it a receipt, and for
// how much). Only the attachments query is invalidated: nothing else on the
// expense reads it, and the edit form is mid-edit while this fires.
export const useUpdateExpenseAttachmentReceipt = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, attachmentId, isReceipt = false, receiptAmount = null }) => {
      if (!expenseId || !attachmentId) throw new Error('Missing attachment target');
      return apiRequest(authenticatedFetch, `/expenses/${expenseId}/attachments/${attachmentId}`, {
        method: 'PATCH',
        body: { is_receipt: isReceipt === true, receipt_amount: receiptAmount },
      });
    },
    onSuccess: (_data, variables) => {
      if (variables?.expenseId) {
        queryClient.invalidateQueries({ queryKey: ['expenses', variables.expenseId, 'attachments'] });
      }
    },
  });
};

// -----------------------------
// Admin Notifications
// -----------------------------
export const useReminderConfig = (scope, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['adminNotifications', 'reminders', scope],
    queryFn: () => apiRequest(authenticatedFetch, `/admin-notifications/reminders/${scope}`),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useSaveReminderConfig = (scope) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config) => apiRequest(authenticatedFetch, `/admin-notifications/reminders/${scope}`, {
      method: 'PUT',
      body: { config }
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['adminNotifications', 'reminders', scope] });
    },
  });
};

export const useVacationApprovalReminderConfig = (options = {}) => useReminderConfig('vacation_approval_reminders', options);
export const useSaveVacationApprovalReminderConfig = () => useSaveReminderConfig('vacation_approval_reminders');

// -----------------------------
// Roles
// -----------------------------
export const useRoles = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['roles'],
    queryFn: () => apiRequest(authenticatedFetch, '/roles'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useRole = (id, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['roles', id],
    queryFn: () => apiRequest(authenticatedFetch, `/roles/${id}`),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useRoleEmployees = (roleId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['roles', roleId, 'employees'],
    queryFn: () => apiRequest(authenticatedFetch, `/roles/${roleId}/employees`),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useAssignRole = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, roleId }) =>
      apiRequest(authenticatedFetch, `/employees/${employeeId}`, { method: 'PUT', body: { role_id: roleId } }),
    onSuccess: (_data, variables) => {
      invalidateEmployeeRecords(queryClient, variables?.employeeId);
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};
// -----------------------------
// Payroll (company-specific module - see config/payrollFeature.js)
// -----------------------------

// Active employees for the payroll picker. Payroll has its own roster endpoint
// rather than reusing /employees, which is gated on manage_all_employees and
// would hand somebody who can only run payroll an empty list.
export const usePayrollEmployees = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'employees'],
    queryFn: () => apiRequest(authenticatedFetch, '/payroll/employees'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

// Pull one pay period and get it back split into Direct / Indirect / Fringe
// Benefits per employee. A mutation rather than a query: it hits TimeCamp, so
// it runs when asked rather than on mount.
export const usePayrollTimesheet = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: ({ start, end, employeeIds }) =>
      apiRequest(authenticatedFetch, '/payroll/timesheet', {
        method: 'POST',
        body: { start, end, employeeIds: employeeIds || [] },
      }),
  });
};

// One employee's election, for the Compensation card on their record. Gated on
// manage_payroll OR manage_all_employees, so the admin who edits the rest of
// the record can edit this too.
export const useEmployeeRetirement = (employeeId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'retirement', employeeId],
    queryFn: () => apiRequest(authenticatedFetch, `/payroll/retirement/${encodeURIComponent(employeeId)}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

// Save one employee's election.
export const useSavePayrollRetirement = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, ...body }) =>
      apiRequest(authenticatedFetch, `/payroll/retirement/${encodeURIComponent(employeeId)}`, {
        method: 'PUT',
        body,
      }),
    // The key prefix covers both the roster read and the per-employee one.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll', 'retirement'] }),
  });
};

// The employer match rate. One rate for the whole company, so it is a settings
// write rather than anything per-employee.
export const useSavePayrollEmployerMatch = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employerMatchPercent) =>
      apiRequest(authenticatedFetch, '/payroll/settings/employer-match', {
        method: 'PUT',
        body: { employerMatchPercent },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll', 'retirement'] }),
  });
};

// Save (or clear) one employee's manual payroll correction for one pay period.
// Corrections are stored against the period, so they come back with it on the
// next pull.
export const useSavePayrollCorrection = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: (body) =>
      apiRequest(authenticatedFetch, '/payroll/corrections', { method: 'PUT', body }),
  });
};

// Saved payroll runs. A run is a frozen snapshot of a finished pay period -
// see server/sql/payroll_runs.sql - so there is no update hook here: a saved
// run is read-only, and redoing a period means deleting it and running again.
export const usePayrollRuns = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'runs'],
    queryFn: () => apiRequest(authenticatedFetch, '/payroll/runs'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

// Load one saved run's full snapshot on demand, when a card is opened.
// Stable across renders, so callers can put it in a dependency list without
// the effect that holds it re-firing on every render.
export const useGetPayrollRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  return useCallback(
    (id) => apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(id)}`),
    [authenticatedFetch]
  );
};

export const useSavePayrollRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => apiRequest(authenticatedFetch, '/payroll/runs', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] }),
  });
};

export const useDeletePayrollRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] }),
  });
};

// Edit the manual correction on a saved run. The server patches the stored
// snapshot and returns it, so nothing is recalculated on this side - a saved
// run is the record, and the client should not be deriving figures for it.
export const useSavePayrollRunCorrection = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ runId, ...body }) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(runId)}/corrections`, {
        method: 'PUT',
        body,
      }),
    // Every mutation that rebuilds a run's figures has to re-ask whether the
    // export is still current - the server's answer changed the moment this
    // wrote. Named explicitly rather than left to the ['payroll', 'runs']
    // prefix, so an `exact: true` added here later cannot silently strand the
    // completion dialog on a stale verdict.
    onSuccess: (_data, { runId }) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', runId, 'exports'] });
    },
  });
};

// One employee's PATO ledger for a year: the grant it started with, then every
// policy change, adjustment and booking against it, each with the balance left
// after it.
export const useEmployeePatoReport = (employeeId, year, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'pato', employeeId, year, 'report'],
    queryFn: () => apiRequest(
      authenticatedFetch,
      `/payroll/pato/${encodeURIComponent(employeeId)}/report?year=${encodeURIComponent(year)}`
    ),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

// The same report for several years at once - one query each, shared with
// useEmployeePatoReport's cache. Returns the query results in `years` order.
export const useEmployeePatoReports = (employeeId, years = [], options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const enabled = (options?.enabled ?? true) && Boolean(employeeId);

  return useQueries({
    queries: years.map((year) => ({
      queryKey: ['payroll', 'pato', employeeId, year, 'report'],
      queryFn: () => apiRequest(
        authenticatedFetch,
        `/payroll/pato/${encodeURIComponent(employeeId)}/report?year=${encodeURIComponent(year)}`
      ),
      ...DEFAULT_QUERY_OPTIONS,
      enabled,
    })),
  });
};

// Every active employee's PATO balance as of each pay period end in a year:
// { year, periods: [{ start, end, inProgress }], employees: [{ id, name,
// allowance, linked, balances, unlimited, used, credited }] } - the last four
// index-aligned with periods. A balance is null when unknown or unlimited.
export const usePatoRoster = (year, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'pato-roster', year],
    queryFn: () => apiRequest(authenticatedFetch, `/payroll/pato-roster?year=${encodeURIComponent(year)}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

// One employee's PATO balance for a year, for the employee record: allowance,
// the year's adjustments one by one, used and remaining - or `unlimited`, with
// no allowance or remaining at all.
export const useEmployeePato = (employeeId, year, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'pato', employeeId, year],
    queryFn: () => apiRequest(
      authenticatedFetch,
      `/payroll/pato/${encodeURIComponent(employeeId)}?year=${encodeURIComponent(year)}`
    ),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

// Everything that moves a PATO balance invalidates every PATO figure: the
// employee's own balance and report, and the roster that charts them all. The
// two keys are siblings, so a 'pato' prefix alone would leave the roster stale.
const invalidatePato = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['payroll', 'pato'] });
  queryClient.invalidateQueries({ queryKey: ['payroll', 'pato-roster'] });
};

// Add a dated credit (positive hours) or debit (negative) to one employee's
// PATO ledger: { employeeId, date, hours, reason }.
export const useAddPatoAdjustment = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, ...body }) =>
      apiRequest(authenticatedFetch, `/payroll/pato/${encodeURIComponent(employeeId)}/adjustments`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidatePato(queryClient),
  });
};

// Void one adjustment. The server keeps the row; it stops counting.
export const useVoidPatoAdjustment = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, adjustmentId, reason }) =>
      apiRequest(
        authenticatedFetch,
        `/payroll/pato/${encodeURIComponent(employeeId)}/adjustments/${encodeURIComponent(adjustmentId)}`,
        { method: 'DELETE', body: reason ? { reason } : undefined }
      ),
    onSuccess: () => invalidatePato(queryClient),
  });
};

// The company PATO policy history: { policies: [{ id, effectiveDate,
// unlimited, hours, notes }], currentId, fallback }.
export const usePatoPolicies = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'pato-policies'],
    queryFn: () => apiRequest(authenticatedFetch, '/payroll/pato-policies'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: options?.enabled ?? true,
    ...options,
  });
};

// { effectiveDate, unlimited, hours, notes }
export const useAddPatoPolicy = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => apiRequest(authenticatedFetch, '/payroll/pato-policies', { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'pato-policies'] });
      invalidatePato(queryClient);
    },
  });
};

export const useRemovePatoPolicy = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (policyId) =>
      apiRequest(authenticatedFetch, `/payroll/pato-policies/${encodeURIComponent(policyId)}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'pato-policies'] });
      invalidatePato(queryClient);
    },
  });
};

// Rename a saved run or write notes on it. The figures are frozen; this is the
// label on the box.
export const useUpdatePayrollRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(id)}`, { method: 'PUT', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] }),
  });
};

// One employee's manual retirement adjustment for a pay period. Two paths, the
// same as corrections: against a saved run the server patches the snapshot and
// returns it; before a run is filed it is written against the period alone.
export const useSavePayrollRetirementAdjustment = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: (body) =>
      apiRequest(authenticatedFetch, '/payroll/retirement-adjustments', { method: 'PUT', body }),
  });
};

export const useSavePayrollRunRetirementAdjustment = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ runId, ...body }) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(runId)}/retirement-adjustments`, {
        method: 'PUT',
        body,
      }),
    // Every mutation that rebuilds a run's figures has to re-ask whether the
    // export is still current - the server's answer changed the moment this
    // wrote. Named explicitly rather than left to the ['payroll', 'runs']
    // prefix, so an `exact: true` added here later cannot silently strand the
    // completion dialog on a stale verdict.
    onSuccess: (_data, { runId }) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', runId, 'exports'] });
    },
  });
};

// One amount and one reason for several employees at once - the group button
// on the Reimbursement Adjustment row. Same response and invalidation as the
// single-cell save below.
export const useSavePayrollRunReimbursementAdjustmentBulk = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ runId, employeeIds, amount, note }) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(runId)}/reimbursement-adjustments/bulk`, {
        method: 'PUT',
        body: { employeeIds, amount, note },
      }),
    onSuccess: (_data, { runId }) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', runId, 'exports'] });
    },
  });
};

// One employee's manual reimbursement adjustment on a saved run - the
// Reimbursement Adjustment row. Returns the patched run, like the retirement
// adjustment, and invalidates the same queries for the same reason.
export const useSavePayrollRunReimbursementAdjustment = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ runId, ...body }) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(runId)}/reimbursement-adjustments`, {
        method: 'PUT',
        body,
      }),
    onSuccess: (_data, { runId }) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', runId, 'exports'] });
    },
  });
};

// Has anything about this run's expenses changed since it was filed?
//
// Deliberately does NOT use DEFAULT_QUERY_OPTIONS. That sets a five minute
// staleTime, which would defeat the whole point: expenses are approved in
// another tab and the answer has to be re-asked the moment this one comes back
// into focus. staleTime 0 plus refetchOnWindowFocus is exactly that - React
// Query's focus manager already listens for both `visibilitychange` and window
// `focus`, so a tab switch and an alt-tab are both covered without a listener
// of our own.
//
// Read-only on the server, so re-asking it on every focus costs nothing but a
// query.
export const usePayrollRunReimbursementDrift = (runId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'runs', runId || null, 'reimbursementDrift'],
    queryFn: () =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(runId)}/reimbursement-drift`),
    enabled: Boolean(runId) && (options.enabled ?? true),
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: 1,
  });
};

// Sign a run off. The last write it takes - see the route for what stops
// taking writes afterwards.
export const useCompletePayrollRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, paymentDate, journalEntryCreated }) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(id)}/complete`, {
        method: 'POST',
        body: { paymentDate, journalEntryCreated },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] }),
  });
};

// Whether TimeCamp has hours this run does not.
//
// Expensive - it re-pulls the period server-side and throws the result away -
// so it is fetched on demand rather than kept fresh: the review dialog asks
// once when it opens. `enabled` is what gates that.
//
// staleTime 0 and no retry: the point is the answer as of the moment the
// dialog opened, and a cached "no drift" from ten minutes ago is exactly the
// answer that would let a changed timesheet through.
export const usePayrollRunTimesheetDrift = (runId, { enabled = true } = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'runs', runId, 'timesheetDrift'],
    queryFn: () => apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(runId)}/timesheet-drift`),
    enabled: Boolean(runId) && enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

// Pull a saved run's period again and overwrite its stored figures.
//
// Never called on its own - the page puts a confirmation in front of it. A
// completed run is refused by the server.
export const useRepullPayrollRun = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(id)}/repull`, {
        method: 'POST',
      }),
    // Every mutation that rebuilds a run's figures has to re-ask whether the
    // export is still current - the server's answer changed the moment this
    // wrote. Named explicitly rather than left to the ['payroll', 'runs']
    // prefix, so an `exact: true` added here later cannot silently strand the
    // completion dialog on a stale verdict.
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', id, 'exports'] });
      // The drift this just resolved is the drift the dialog is showing.
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', id, 'timesheetDrift'] });
    },
  });
};

// The export trail for a run: every time the workbook was downloaded, and by
// whom. The review dialog gates completion on this being non-empty.
//
// `enabled` so it is only fetched for a run that exists - the page holds no
// run id until one is saved.
export const usePayrollRunExports = (runId) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['payroll', 'runs', runId, 'exports'],
    queryFn: () => apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(runId)}/exports`),
    enabled: Boolean(runId),
  });
};

// Record that the workbook was downloaded. Called after the file is handed to
// the browser, never before - see the route.
export const useRecordPayrollRunExport = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, filename, figuresVersion }) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(id)}/exports`, {
        method: 'POST',
        body: { filename, figuresVersion },
      }),
    onSuccess: (_data, { id }) =>
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', id, 'exports'] }),
  });
};

// Whether the export has been uploaded to SharePoint.
//
// Not part of the freeze, for the same reason the journal entry flag is not:
// the upload happens after the sign-off. See the route.
export const useSetPayrollRunSharePoint = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, confirmed }) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(id)}/sharepoint`, {
        method: 'PUT',
        body: { confirmed },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] }),
  });
};

// Whether the QuickBooks journal entry for a run has been created.
//
// Not part of the freeze: a completed run still takes this one write, because
// the entry is normally keyed in after the run is signed off. See the route.
export const useSetPayrollRunJournalEntry = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, created }) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(id)}/journal-entry`, {
        method: 'PUT',
        body: { created },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] }),
  });
};

export const useRefreshPayrollRunReimbursements = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (runId) =>
      apiRequest(authenticatedFetch, `/payroll/runs/${encodeURIComponent(runId)}/reimbursements/refresh`, {
        method: 'POST',
      }),
    onSuccess: (_data, runId) => {
      // The drift the dialog was showing is the drift that was just closed, so
      // the badge has to be re-asked rather than left on screen.
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', runId, 'reimbursementDrift'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'], exact: true });
      // This one rebuilds the run's figures, so the export verdict moved with
      // it. The `exact: true` above deliberately does not reach the run's own
      // keys, which is why this is spelled out.
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', runId, 'exports'] });
    },
  });
};
